import axios from "axios";
import { storedSessionId } from "@/shared/lib/api-client";
import type { Attachment, Message } from "../types";

export class ChatApiError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * Adds the stored session id as `?sessionid=`, as the funnel's api-client does:
 * the server falls back to it when Safari drops the HttpOnly cookie.
 */
export function withSessionId(url: string): string {
  const sessionid = storedSessionId();
  if (!sessionid) return url;
  const hashAt = url.indexOf("#");
  const path = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}sessionid=${encodeURIComponent(sessionid)}${hash}`;
}

/** Same-origin request; the server checks the Origin allowlist, then the session cookie or `?sessionid=`. */
export async function chatRequest<T>(
  url: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const multipart = body instanceof FormData;
  const response = await fetch(withSessionId(url), {
    method,
    credentials: "same-origin",
    signal: signal
      ? AbortSignal.any([signal, AbortSignal.timeout(30000)])
      : AbortSignal.timeout(30000),
    headers: {
      ...(!multipart ? { "Content-Type": "application/json" } : {}),
    },
    ...(body === undefined
      ? {}
      : { body: multipart ? body : JSON.stringify(body) }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new ChatApiError(
      data?.message ??
        data?.error?.message ??
        "Connection interrupted. Please try again.",
      response.status,
      data?.code ?? data?.error?.code ?? "unavailable",
    );
  }
  return data.result as T;
}

export function mergeMessages(
  current: Message[],
  incoming: Message[],
): Message[] {
  const messages = new Map(current.map((message) => [message.rootId, message]));
  for (const message of incoming) {
    const previous = messages.get(message.rootId);
    if (!previous || BigInt(message.shownSeq) >= BigInt(previous.shownSeq)) {
      messages.set(message.rootId, message);
    }
  }
  return [...messages.values()].sort((left, right) =>
    BigInt(left.rootId) < BigInt(right.rootId)
      ? -1
      : BigInt(left.rootId) > BigInt(right.rootId)
        ? 1
        : 0,
  );
}

/** XHR-backed uploads expose real byte progress. */
export async function uploadChatMedia(
  url: string,
  file: File,
  signal: AbortSignal,
  onProgress: (percent: number | null) => void,
) {
  const body = new FormData();
  body.set("file", file);
  try {
    const response = await axios.post<{
      success: boolean;
      result: { attachment: Attachment };
      message?: string;
      code?: string;
      error?: { message?: string; code?: string };
    }>(withSessionId(url), body, {
      adapter: "xhr",
      signal,
      timeout: 30000,
      validateStatus: () => true,
      onUploadProgress: (event) =>
        onProgress(
          event.total
            ? Math.min(100, Math.round((event.loaded / event.total) * 100))
            : null,
        ),
    });
    const data = response.data;
    if (response.status < 200 || response.status >= 300 || !data?.success)
      throw new ChatApiError(
        data?.message ??
          data?.error?.message ??
          "Upload failed. Please try again.",
        response.status,
        data?.code ?? data?.error?.code ?? "unavailable",
      );
    return data.result;
  } catch (error) {
    if (error instanceof ChatApiError) throw error;
    throw new Error(
      signal.aborted
        ? "Upload cancelled."
        : "Upload interrupted. Choose the file again to retry.",
    );
  }
}
